import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../services/api';
import {
  emptyRealtimeTranscript,
  realtimeTranscriptText,
  reduceRealtimeTranscript,
  RealtimeTranscriptEvent,
  RealtimeTranscriptState,
} from './realtimeDictationTranscript';

type DictationStatus = 'idle' | 'starting' | 'listening';

type UseRealtimeDictationOptions = {
  prompt?: string;
  onTranscript: (transcript: string) => void;
};

const OPENAI_REALTIME_CALLS_URL = 'https://api.openai.com/v1/realtime/calls';

const getDictationErrorMessage = (error: unknown): string => {
  if (error instanceof DOMException) {
    if (error.name === 'NotAllowedError' || error.name === 'SecurityError') {
      return 'Microphone access was denied.';
    }
    if (error.name === 'NotFoundError') {
      return 'No microphone was found.';
    }
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return 'Could not start dictation.';
};

export const useRealtimeDictation = ({
  prompt,
  onTranscript,
}: UseRealtimeDictationOptions) => {
  const [status, setStatus] = useState<DictationStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const dataChannelRef = useRef<RTCDataChannel | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const transcriptRef = useRef<RealtimeTranscriptState>(
    emptyRealtimeTranscript(),
  );
  const onTranscriptRef = useRef(onTranscript);
  const statusRef = useRef<DictationStatus>('idle');
  const attemptRef = useRef(0);

  onTranscriptRef.current = onTranscript;

  const updateStatus = useCallback((nextStatus: DictationStatus) => {
    statusRef.current = nextStatus;
    setStatus(nextStatus);
  }, []);

  const closeResources = useCallback(() => {
    const dataChannel = dataChannelRef.current;
    const peerConnection = peerConnectionRef.current;
    const mediaStream = mediaStreamRef.current;

    dataChannelRef.current = null;
    peerConnectionRef.current = null;
    mediaStreamRef.current = null;

    dataChannel?.close();
    peerConnection?.close();
    mediaStream?.getTracks().forEach(track => track.stop());
  }, []);

  const stop = useCallback(() => {
    attemptRef.current += 1;
    closeResources();
    updateStatus('idle');
  }, [closeResources, updateStatus]);

  const fail = useCallback((message: string) => {
    attemptRef.current += 1;
    closeResources();
    updateStatus('idle');
    setError(message);
  }, [closeResources, updateStatus]);

  const start = useCallback(async () => {
    if (statusRef.current !== 'idle') return;
    if (
      typeof RTCPeerConnection === 'undefined'
      || !navigator.mediaDevices?.getUserMedia
    ) {
      setError('Realtime dictation is not supported in this browser.');
      return;
    }

    const attempt = attemptRef.current + 1;
    attemptRef.current = attempt;
    transcriptRef.current = emptyRealtimeTranscript();
    setError(null);
    updateStatus('starting');

    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
      if (attemptRef.current !== attempt) {
        mediaStream.getTracks().forEach(track => track.stop());
        return;
      }
      mediaStreamRef.current = mediaStream;

      const { client_secret: clientSecret } =
        await api.createRealtimeTranscriptionToken(prompt);
      if (attemptRef.current !== attempt) return;

      const peerConnection = new RTCPeerConnection();
      peerConnectionRef.current = peerConnection;
      mediaStream.getAudioTracks().forEach(track => {
        peerConnection.addTrack(track, mediaStream);
      });

      const dataChannel = peerConnection.createDataChannel('oai-events');
      dataChannelRef.current = dataChannel;

      dataChannel.addEventListener('open', () => {
        if (
          attemptRef.current === attempt
          && dataChannelRef.current === dataChannel
        ) {
          updateStatus('listening');
        }
      });

      dataChannel.addEventListener('message', event => {
        if (attemptRef.current !== attempt) return;
        let realtimeEvent: RealtimeTranscriptEvent & {
          error?: { message?: string };
        };
        try {
          realtimeEvent = JSON.parse(event.data);
        } catch {
          return;
        }

        if (realtimeEvent.type === 'error') {
          fail(
            realtimeEvent.error?.message
              || 'The transcription session reported an error.',
          );
          return;
        }

        const nextTranscript = reduceRealtimeTranscript(
          transcriptRef.current,
          realtimeEvent,
        );
        if (nextTranscript === transcriptRef.current) return;
        transcriptRef.current = nextTranscript;
        onTranscriptRef.current(realtimeTranscriptText(nextTranscript));
      });

      dataChannel.addEventListener('close', () => {
        if (
          dataChannelRef.current === dataChannel
          && statusRef.current !== 'idle'
        ) {
          fail('The dictation session ended.');
        }
      });

      peerConnection.addEventListener('connectionstatechange', () => {
        if (
          peerConnectionRef.current === peerConnection
          && peerConnection.connectionState === 'failed'
        ) {
          fail('The dictation connection was lost.');
        }
      });

      const offer = await peerConnection.createOffer();
      await peerConnection.setLocalDescription(offer);
      if (attemptRef.current !== attempt) return;

      const response = await fetch(OPENAI_REALTIME_CALLS_URL, {
        method: 'POST',
        body: peerConnection.localDescription?.sdp || offer.sdp,
        headers: {
          Authorization: `Bearer ${clientSecret}`,
          'Content-Type': 'application/sdp',
        },
      });
      if (!response.ok) {
        throw new Error('OpenAI could not establish the dictation session.');
      }

      const answerSdp = await response.text();
      if (attemptRef.current !== attempt) return;
      await peerConnection.setRemoteDescription({
        type: 'answer',
        sdp: answerSdp,
      });
    } catch (startError) {
      if (attemptRef.current !== attempt) return;
      fail(getDictationErrorMessage(startError));
    }
  }, [fail, prompt, updateStatus]);

  useEffect(() => () => {
    attemptRef.current += 1;
    closeResources();
  }, [closeResources]);

  const isSupported = typeof RTCPeerConnection !== 'undefined'
    && typeof navigator !== 'undefined'
    && Boolean(navigator.mediaDevices?.getUserMedia);

  return {
    error,
    isActive: status !== 'idle',
    isListening: status === 'listening',
    isStarting: status === 'starting',
    isSupported,
    start,
    stop,
  };
};
